import * as React from 'react';
import classnames from 'classnames';
import {
  OCCUPIED_DURATIONS_SECONDS,
  OccupiedDurationSeconds,
  PRESENCE_COLORS,
  PresenceState,
  PresenceStatus,
} from '@ixicare/resources';
import { t } from '~i18n';
import StatusBadge, { toDate } from './StatusBadge';
import styles from './StatusPicker.module.css';

export interface StatusPickerProps {
  currentStatus: PresenceStatus | null;
  /** The exact rung the user last picked in-session, if any. Source of truth
   *  for the active highlight — backend only returns `occupiedUntil`, which
   *  shifts as time passes. */
  selectedDuration?: OccupiedDurationSeconds | null;
  onSelectOccupied: (durationSeconds: OccupiedDurationSeconds) => void;
  onSelectUnavailable: () => void;
  onClear: () => void;
  className?: string;
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * 60;

function durationLabel(seconds: OccupiedDurationSeconds): string {
  if (seconds < SECONDS_PER_HOUR) {
    return t('presence.picker.busyMinutes', { count: Math.round(seconds / SECONDS_PER_MINUTE) });
  }
  const hours = Math.round(seconds / SECONDS_PER_HOUR);
  return hours === 1
    ? t('presence.picker.busyHour', { count: hours })
    : t('presence.picker.busyHours', { count: hours });
}

function useOutsideClick<T extends HTMLElement>(
  ref: React.RefObject<T | null>,
  onOutside: () => void,
  enabled: boolean
) {
  React.useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onOutside();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside, enabled]);
}

const StatusPicker: React.FC<StatusPickerProps> = ({
  currentStatus,
  selectedDuration = null,
  onSelectOccupied,
  onSelectUnavailable,
  onClear,
  className,
}) => {
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLUListElement>(null);

  const close = React.useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useOutsideClick(wrapperRef, () => setOpen(false), open);

  const handleOnline = () => {
    close();
    onClear();
  };
  const handleOccupied = (seconds: OccupiedDurationSeconds) => {
    close();
    onSelectOccupied(seconds);
  };
  const handleUnavailable = () => {
    close();
    onSelectUnavailable();
  };

  const state = currentStatus?.state ?? PresenceState.Offline;
  const isOnlineSelected = state === PresenceState.Online || state === PresenceState.Offline;
  const isUnavailableSelected = state === PresenceState.Unavailable;

  // Stable primitive for memo deps — `toDate(...)` returns a fresh Date each
  // render and would defeat caching.
  const occupiedUntilMs = toDate(currentStatus?.occupiedUntil ?? null)?.getTime() ?? null;

  // Which Occupied rung to highlight. Prefer the in-session pick; otherwise
  // (page reload, cross-device sync) snap to the rung closest to the remaining
  // time so the active rung is always identifiable.
  const activeRungSeconds: OccupiedDurationSeconds | null = React.useMemo(() => {
    if (state !== PresenceState.Occupied) return null;
    if (selectedDuration != null) return selectedDuration;
    if (occupiedUntilMs == null) return null;
    const remainingSec = Math.max(0, Math.round((occupiedUntilMs - Date.now()) / 1000));
    let best: OccupiedDurationSeconds = OCCUPIED_DURATIONS_SECONDS[0];
    let bestDiff = Math.abs(best - remainingSec);
    for (const rung of OCCUPIED_DURATIONS_SECONDS) {
      const diff = Math.abs(rung - remainingSec);
      if (diff < bestDiff) {
        best = rung;
        bestDiff = diff;
      }
    }
    return best;
  }, [state, selectedDuration, occupiedUntilMs]);

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button[role="option"]') ?? []);
    if (items.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? items.indexOf(active as HTMLButtonElement) : -1;
    const next =
      e.key === 'ArrowDown' ? items[(idx + 1) % items.length] : items[(idx - 1 + items.length) % items.length];
    next?.focus();
  };

  React.useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector<HTMLButtonElement>('button[role="option"]');
    first?.focus();
  }, [open]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <div className={classnames(styles.Wrapper, className)} ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className={classnames(styles.Trigger, isUnavailableSelected && styles.TriggerUnavailable)}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleTriggerKeyDown}
      >
        {isUnavailableSelected && (
          <span className={styles.Warning} aria-hidden="true">
            ⚠
          </span>
        )}
        <StatusBadge status={currentStatus} />
        <span className={styles.Caret} aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <ul ref={menuRef} className={styles.Menu} role="listbox" onKeyDown={handleMenuKeyDown}>
          <li>
            <button
              type="button"
              role="option"
              aria-selected={isOnlineSelected}
              className={styles.MenuItem}
              onClick={handleOnline}
            >
              <span className={styles.ItemDot} style={{ background: PRESENCE_COLORS[PresenceState.Online] }} />
              <span>{t('presence.state.online')}</span>
            </button>
          </li>
          {OCCUPIED_DURATIONS_SECONDS.map((seconds) => {
            const isSelected = activeRungSeconds === seconds;
            return (
              <li key={seconds}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={styles.MenuItem}
                  onClick={() => handleOccupied(seconds)}
                >
                  <span className={styles.ItemDot} style={{ background: PRESENCE_COLORS[PresenceState.Occupied] }} />
                  <span>{durationLabel(seconds)}</span>
                </button>
              </li>
            );
          })}
          <li className={styles.Divider} aria-hidden="true" />
          <li>
            <button
              type="button"
              role="option"
              aria-selected={isUnavailableSelected}
              className={styles.MenuItem}
              onClick={handleUnavailable}
            >
              <span className={styles.ItemDot} style={{ background: PRESENCE_COLORS[PresenceState.Unavailable] }} />
              <span>{t('presence.state.unavailable')}</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

export default StatusPicker;
